/**
 * Frontend types for the wonders-table mode. Mirror the platform's
 * @wonders/games-wonders shapes but redeclared here so the bundle
 * doesn't pull in the entire game-engine package.
 */

export type RealmName =
  | 'Petraia'
  | 'Solfera'
  | 'Thalwind'
  | 'Umbrathene'
  | 'Heliosynth'
  | 'Boundless'
  | 'Clear';

export const REALMS: readonly RealmName[] = [
  'Petraia',
  'Solfera',
  'Thalwind',
  'Umbrathene',
  'Heliosynth',
  'Boundless',
  'Clear',
];

/**
 * Tailwind background-color class for a realm/orbital. Used as a 4-px
 * left-edge strip on deck tiles to give an at-a-glance read of the
 * deck's dominant orbital. The custom colors (`petraia`, `solfera`,
 * etc.) are defined in the Tailwind config — see RealmTile.tsx for the
 * gradient variants used on the play surface.
 */
const ORBITAL_BG: Record<RealmName, string> = {
  Petraia: 'bg-petraia',
  Solfera: 'bg-solfera',
  Thalwind: 'bg-thalwind',
  Umbrathene: 'bg-umbrathene',
  Heliosynth: 'bg-heliosynth',
  Boundless: 'bg-boundless',
  Clear: 'bg-clear',
};

export function orbitalColor(name: RealmName | null | undefined): string {
  if (!name) return 'bg-cosmos-600';
  return ORBITAL_BG[name] ?? 'bg-cosmos-600';
}

/**
 * Given a deck's card ids and a card catalog, return the most common
 * orbital across those cards. Used to drive the orbital-color strip on
 * deck tiles. Falls back to null when no card has an orbital tag.
 */
export function dominantOrbital(
  cardIds: readonly string[],
  catalog: ReadonlyMap<string, { orbital?: string | null }>,
): RealmName | null {
  const counts = new Map<RealmName, number>();
  for (const id of cardIds) {
    const card = catalog.get(id);
    const o = card?.orbital;
    if (!o) continue;
    if (!REALMS.includes(o as RealmName)) continue;
    counts.set(o as RealmName, (counts.get(o as RealmName) ?? 0) + 1);
  }
  let best: RealmName | null = null;
  let bestN = 0;
  for (const [name, n] of counts) {
    if (n > bestN) {
      best = name;
      bestN = n;
    }
  }
  return best;
}

export type CardId = string;
export type PlayerId = string;

// ─── Cards (loaded from /api/cards) ─────────────────────────────────

export type CardData = {
  card_id: CardId;
  game_id: string;
  set_id: string;
  name: string;
  raw_text: string;
  parsed_abilities: readonly unknown[];
  ingest_version: string;
  eval_status: string;
  /** v1.7 fields surfaced by /api/cards. All optional — older snapshots
   *  may have null/undefined. Used by the hover detail panel. */
  cost?: number | null;
  power?: number | null;
  card_type?: string | null;
  rarity?: string | null;
  orbital?: string | null;
  keywords?: readonly string[] | null;
};

// ─── Decks ──────────────────────────────────────────────────────────

export type DeckData = {
  deck_id: string;
  game_id: string;
  name: string;
  owner_user_id: string | null;
  is_tournament: boolean;
  tournament_name?: string;
  tournament_date?: number;
  card_ids: readonly string[];
  visibility: 'public' | 'private';
  created_at: number;
  updated_at: number;
};

// ─── Game-state shapes (mirrors WondersTableState) ──────────────────

export type Placement = {
  cardId: CardId;
  owner: PlayerId;
  tapped: boolean;
  faceDown: boolean;
  x: number;
  y: number;
  counters: Readonly<Record<string, number>>;
};

export type RealmZone = {
  name: RealmName;
  placements: readonly Placement[];
  stone: PlayerId | null;
};

export type PlayerZones = {
  hand: readonly CardId[];
  deck: readonly CardId[];
  abyss: readonly CardId[];
  banished: readonly CardId[];
  /** Non-realm permanents (items, artifacts, CotS stone-item cards). */
  kingdom: readonly Placement[];
};

export type PerPlayerResources = {
  energy: number;
  actions: number;
  hoard: number;
};

export type RoundPip = 1 | 0 | null;

export type ChatEntry = {
  at: number;
  actor: PlayerId;
  text: string;
};

export type DiceRoll = {
  at: number;
  actor: PlayerId;
  sides: number;
  result: number;
};

export type MatchOutcome = {
  declaredBy: PlayerId;
  kind: 'concede' | 'declare_victory';
  note?: string;
  endedAt: number;
};

export type WondersTableState = {
  players: readonly [PlayerId, PlayerId];
  zones: Readonly<Record<PlayerId, PlayerZones>>;
  realms: readonly [RealmZone, RealmZone, RealmZone, RealmZone, RealmZone, RealmZone, RealmZone];
  chat: readonly ChatEntry[];
  dice: readonly DiceRoll[];
  outcome: MatchOutcome | null;
};

// ─── TableAction (what the client emits) ─────────────────────────────

export type ZoneRef =
  | { kind: 'hand'; owner: PlayerId }
  | { kind: 'deck'; owner: PlayerId; position?: 'top' | 'bottom' }
  | { kind: 'abyss'; owner: PlayerId; position?: 'top' | 'bottom' }
  | { kind: 'banished'; owner: PlayerId; position?: 'top' | 'bottom' }
  | { kind: 'tokens'; owner: PlayerId; position?: 'top' | 'bottom' }
  | {
      kind: 'realm';
      realm: RealmName;
      owner: PlayerId;
      x?: number;
      y?: number;
    };

export type TableAction =
  | { kind: 'MoveCard'; cardId: CardId; from: ZoneRef; to: ZoneRef }
  | { kind: 'TapCard'; cardId: CardId }
  | { kind: 'UntapCard'; cardId: CardId }
  | { kind: 'FlipCard'; cardId: CardId; faceDown?: boolean }
  | { kind: 'AddCounter'; cardId: CardId; counter: string; n: number }
  | { kind: 'RemoveCounter'; cardId: CardId; counter: string; n: number }
  | { kind: 'MoveStone'; realm: RealmName; to: PlayerId | null }
  | { kind: 'RollDice'; sides: number; result: number }
  | { kind: 'ChatMessage'; text: string }
  | { kind: 'EmoteReaction'; emoji: string; x?: number; y?: number }
  | { kind: 'AnnotateMove'; note: string }
  | { kind: 'Mulligan'; cardIds: readonly CardId[] }
  | { kind: 'AdvanceRound'; to?: number }
  | { kind: 'PassTurn' }
  | { kind: 'AdjustResource'; resource: 'energy' | 'actions' | 'hoard'; delta: number }
  | {
      kind: 'RequestZoneEdit';
      request_id: string;
      request_kind: 'reorder' | 'inspect' | 'move-to';
      target_zone: ZoneRef;
      note?: string;
    }
  | { kind: 'ResolveZoneEdit'; request_id: string; decision: 'approve' | 'deny' }
  | { kind: 'DeclareVictory'; note?: string }
  | { kind: 'ConcedeMatch'; note?: string }
  | { kind: 'Undo' };

export type TableMove = { kind: 'TableAction'; action: TableAction };

// ─── Public state shape (as broadcast via publicState) ───────────────
//
// The server-side publicState may redact opponent private zones and
// face-down cards. The frontend renders from this shape directly.

export type PublicTableState = {
  /** Match mode — 'sandbox' (permissive) or 'ranked' (rule-enforcing). */
  mode?: 'sandbox' | 'ranked';
  players: readonly [PlayerId, PlayerId];
  /** Map of player → either full PlayerZones (for the viewer) or
   * a redacted view with counts only (for the opponent). */
  zones: Readonly<
    Record<
      PlayerId,
      | PlayerZones
      | {
          handCount: number;
          deckCount: number;
          abyss: readonly CardId[];
          banished: readonly CardId[];
          kingdom?: readonly Placement[];
        }
    >
  >;
  realms: readonly RealmZone[];
  chat: readonly ChatEntry[];
  dice: readonly DiceRoll[];
  outcome: MatchOutcome | null;
  /** Round 1..7 (ranked) or 0 (sandbox). */
  round?: number;
  /** Whose turn in ranked mode; null in sandbox. */
  activePlayer?: PlayerId | null;
  /** Per-player resources — energy/actions/hoard. */
  resources?: Readonly<Record<PlayerId, PerPlayerResources>>;
  /** Per-player length-7 pip outcomes (1=won that round, 0=lost, null=unplayed). */
  roundPips?: Readonly<Record<PlayerId, readonly RoundPip[]>>;
  /** Per-player one-shot mulligan flag — true once used or skipped. */
  mulliganUsed?: Readonly<Record<PlayerId, boolean>>;
  /** d20 rolls per player; the higher result wins first-player priority. */
  firstPlayerRoll?: Readonly<Record<PlayerId, number | null>>;
  firstPlayer?: PlayerId | null;
  /** Whose turn it is to play. Flips on each card-play (MoveCard from
   *  hand → realm); resets to firstPlayer at AdvanceRound. */
  initiative?: PlayerId | null;
  /** Cross-player zone-edit requests awaiting owner approval. Modal
   *  surfaces in the target_owner's UI when their player_id matches. */
  pendingApprovals?: Readonly<
    Record<
      string,
      {
        request_kind: 'reorder' | 'inspect' | 'move-to';
        target_zone:
          | { kind: 'deck' | 'abyss' | 'banished'; owner: PlayerId }
          | { kind: 'realm'; realm: string; owner: PlayerId };
        target_owner: PlayerId;
        requester: PlayerId;
        note?: string;
        requested_at: number;
      }
    >
  >;
  /** Snapshot stack for the Undo flow. Opaque to the client; the
   *  reducer on the server is the only thing that reads its contents. */
  undoHistory?: readonly unknown[];
  /** Set when an Undo request is awaiting opponent confirmation. */
  pendingUndo?: { requestedBy: PlayerId; requestedAt: number } | null;
  /** Who took the most recent undoable action — drives the Undo
   *  button label / disabled state in the chrome bar. */
  lastActor?: PlayerId | null;
};
